import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';

/**
 * A single read-only label/value pair.
 *
 * These panels used to be built from `<TextField disabled value={...} />`.
 * That was wrong in three ways:
 *
 * 1. The label overlapped the value. MUI captures whether an input is
 *    controlled in a ref on its *first* render (`useRef(value != null)`).
 *    Detail cards render once before their fetch resolves, so `value` was
 *    `undefined` and the field was marked uncontrolled for good — the effect
 *    that floats the label on an external value change never ran again, and
 *    the label stayed sitting on top of the text once it arrived. Fields whose
 *    formatter returned a string for `undefined` (fDate, fCurrency) happened to
 *    escape it, which is why only some rows looked broken.
 * 2. A disabled input cannot be focused or selected, so none of the values
 *    could be copied, and screen readers announce them as unavailable form
 *    controls rather than as content.
 * 3. Disabled text renders at MUI's disabled contrast, which fails WCAG AA.
 *
 * Rendering a description list sidesteps all of it: the label is always above
 * the value because it is a separate element, and there is no input state to
 * get out of sync with the data.
 */
export default function DetailField({ label, value, placeholder, multiline, sx }) {
  const isEmpty = value === null || value === undefined || value === '';

  return (
    <Box
      sx={{
        px: 1.75,
        py: 1.25,
        minHeight: 56,
        borderRadius: 1,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        ...sx,
      }}
    >
      <Typography
        component="dt"
        variant="caption"
        sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}
      >
        {label}
      </Typography>
      <Typography
        component="dd"
        variant="body2"
        sx={{
          m: 0,
          mt: 0.25,
          color: isEmpty ? 'text.disabled' : 'text.primary',
          fontWeight: isEmpty ? 400 : 500,
          whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
          overflow: multiline ? 'visible' : 'hidden',
          textOverflow: multiline ? 'clip' : 'ellipsis',
          // Values are content, so they stay selectable and copyable.
          userSelect: 'text',
        }}
        title={!multiline && !isEmpty ? String(value) : undefined}
      >
        {isEmpty ? placeholder : value}
      </Typography>
    </Box>
  );
}

DetailField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  placeholder: PropTypes.string,
  multiline: PropTypes.bool,
  sx: PropTypes.object,
};

DetailField.defaultProps = {
  value: null,
  placeholder: '—',
  multiline: false,
  sx: null,
};
